import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FaEnvelope, FaCheck } from 'react-icons/fa';
import './NewsletterSignup.css';
// newsletter badge: isolated in its own hook file for easy removal later
// (see the comment at the top of useNewsletterIconBadge.js).
import { useNewsletterIconBadge } from './useNewsletterIconBadge';

// Google Forms accepts a plain POST to its `/formResponse` endpoint without
// any API key. Fill these in once the real form exists (see README TODO).
const FORM_ACTION_URL = process.env.REACT_APP_NEWSLETTER_FORM_ACTION_URL;
const EMAIL_ENTRY_ID = process.env.REACT_APP_NEWSLETTER_EMAIL_ENTRY_ID;
const IS_CONFIGURED = Boolean(FORM_ACTION_URL && EMAIL_ENTRY_ID);

const NewsletterSignup = React.memo(function NewsletterSignup() {
  const [isOpen, setIsOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const containerRef = useRef(null);
  const emailInputRef = useRef(null);
  // newsletter badge
  const [showUnclickedBadge, dismissUnclickedBadge] = useNewsletterIconBadge();

  const toggleOpen = useCallback(() => {
    dismissUnclickedBadge(); // newsletter badge: cleared on click, not on form submit
    setIsOpen((open) => !open);
  }, [dismissUnclickedBadge]);

  // Move focus into the email field as soon as the dropdown opens, so
  // keyboard/screen-reader users don't have to tab in manually.
  useEffect(() => {
    if (isOpen && status !== 'success') {
      emailInputRef.current?.focus();
    }
  }, [isOpen, status]);

  useEffect(() => {
    if (!isOpen) return;
    const handleOutsideClick = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    const handleEscape = (e) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    // Pointer events (not mousedown) so this fires reliably for touch on iOS
    // Safari, and the capture phase so it still runs even when something
    // tapped in between (e.g. an AppWindow being dragged via react-rnd)
    // stops the event from bubbling up to document.
    document.addEventListener('pointerdown', handleOutsideClick, true);
    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('pointerdown', handleOutsideClick, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen]);

  const handleSubmit = useCallback(async (e) => {
    e.preventDefault();
    if (!IS_CONFIGURED) {
      setStatus('error');
      return;
    }
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setStatus('error');
      return;
    }
    setStatus('submitting');

    const body = new URLSearchParams();
    body.append(EMAIL_ENTRY_ID, trimmedEmail);

    try {
      // Google Forms doesn't send CORS headers, so the response is opaque
      // ('no-cors') — we can't read status/ok back. A resolved promise here
      // means the request went out, not that Google accepted it.
      await fetch(FORM_ACTION_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body,
      });
      setStatus('success');
      setEmail('');
    } catch {
      setStatus('error');
    }
  }, [email]);

  return (
    <div className="newsletter-signup" ref={containerRef}>
      <button
        className="newsletter-icon-btn"
        onClick={toggleOpen}
        aria-label="Sign up for email updates"
        aria-expanded={isOpen}
      >
        <FaEnvelope />
        {/* newsletter badge */}
        {showUnclickedBadge && (
          <span className="newsletter-unclicked-badge" aria-hidden="true">!</span>
        )}
      </button>
      {isOpen && (
        <div className="newsletter-dropdown">
          <div className="newsletter-titlebar">
            <span className="newsletter-titlebar-label">Mailing List</span>
          </div>
          <div className="newsletter-dropdown-content">
            {status === 'success' ? (
              <p className="newsletter-message newsletter-message--success" role="status">
                <span className="newsletter-success-icon">
                  <FaCheck />
                </span>
                You're on the list. Thanks!
              </p>
            ) : (
              <form onSubmit={handleSubmit}>
                <label className="newsletter-heading" htmlFor="newsletter-email">
                  Get email updates from The Two Jakes
                </label>
                <input
                  id="newsletter-email"
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="email"
                  autoCapitalize="none"
                  autoCorrect="off"
                  spellCheck="false"
                  required
                  placeholder="you@example.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={status === 'submitting'}
                  ref={emailInputRef}
                />
                <button
                  type="submit"
                  className="retro-btn newsletter-submit-btn"
                  disabled={status === 'submitting'}
                >
                  {status === 'submitting' ? 'Signing up...' : 'Sign Up'}
                </button>
                {status === 'error' && (
                  <p className="newsletter-message newsletter-message--error" role="alert">
                    {IS_CONFIGURED
                      ? "Something went wrong. Try again in a bit."
                      : 'Signup form is not configured yet.'}
                  </p>
                )}
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
});

export default NewsletterSignup;
